// @ai-generated 2026-07-10T00:00:00Z by matthias.gessinger@synqony.com using Claude Sonnet 5
import { AUFTRAG_API } from '@Constants/backendConstants';
import { type ApiAddress } from '@Model';
import {
    type Awaitable,
    EnumDirection,
    EnumFilterExpressionOperation,
    EnumOperation,
    type Index,
    type ListInfo,
    type ListRequest,
    useLinkableSearch,
} from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, type UIEvent } from 'react';
import { useListApi } from '@Hooks/useListApi';
import { RequisitionsRepository } from '@Repositories/Auftragsbearbeitung';

const fullList = (pageSize: number): ListRequest => ({
    page_no: 0,
    page_size: pageSize,
    full_text_search: '',
    sort_columns: [],
});

/** How far back the picker looks for the sites it badges as recently used. */
const RECENT_REQUISITIONS = 20;

const idField: Index<ApiAddress> = 'id';

export interface SiteAddresses {
    /** Addresses matching the current search — the pages loaded so far, recently used ones first. */
    addresses: ApiAddress[];
    isLoading: boolean;
    isFetching: boolean;
    searchValue: string;
    setSearchValue: (value: string) => void;
    /** How many match on the server, which is more than {@link addresses} until scrolled to the end. */
    totalCount: number;
    hasMore: boolean;
    /** Must be attached to the list, otherwise only the first page is reachable. */
    onScroll: (event: UIEvent<HTMLDivElement>) => void;
    /** Ids of addresses used as site in the most recent requisitions. */
    recentlyUsedIds: ReadonlySet<string>;
}

/**
 * Narrows a list request to the given ids, keeping the filters it already carries.
 *
 * @ai-generated 2026-08-27T12:14:34+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
function restrictToIds(listRequest: ListRequest, ids: string[]): ListRequest {
    return {
        ...listRequest,
        page_no: 0,
        page_size: ids.length,
        // The id alternatives must be nested inside an "and", never become the root: the backend
        // appends the link-specific filters (site role, business state) to whatever root arrives
        // here, and an "or" root would turn those requirements into further alternatives.
        filter_columns: {
            operation: EnumFilterExpressionOperation.And,
            filters: [
                ...(listRequest.filter_columns ? [listRequest.filter_columns] : []),
                {
                    operation: EnumFilterExpressionOperation.Or,
                    filters: ids.map((id) => ({
                        field: idField,
                        value: id,
                        operator: EnumOperation.Equal,
                    })),
                },
            ],
        },
    };
}

/**
 * Brings the addresses back into the order of {@link ids}, which the server does not preserve.
 *
 * @ai-generated 2026-08-27T12:14:34+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
function inOrderOf(ids: string[], addresses: ApiAddress[]): ApiAddress[] {
    return ids
        .map((id) => addresses.find((address) => address.id === id))
        .filter((address): address is ApiAddress => address !== undefined);
}

/**
 * Drives the site-address search (via the same `linkSite.loadLinkableObjects` the actual
 * link uses) plus the set of addresses used by the most recent requisitions, so the picker
 * can badge them as last used and show them first.
 *
 * @ai-generated 2026-08-27T12:14:34+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
 */
export function useSiteAddresses(
    loadLinkableAddresses: (tr: ListRequest) => Awaitable<ListInfo<ApiAddress>>,
): SiteAddresses {
    const { loadPage: loadRequisitions } = useListApi(RequisitionsRepository, AUFTRAG_API, ['Site']);

    /**
     * The sites of the most recent requisitions.
     *
     * A cached query rather than a request fired from an effect: the picker is mounted with the
     * step around it and the answer is the same every time, so reading it raw turns every mount —
     * and, in development, every doubled effect — into another identical `requisitions:query`.
     */
    // @ai-generated 2026-09-11T16:40:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
    const { data, isPending } = useQuery({
        queryFn: async () => {
            const requisitionList = await loadRequisitions({
                ...fullList(RECENT_REQUISITIONS),
                sort_columns: [{ column_name: 'created_at', direction: EnumDirection.DESC }],
            });
            const siteIds = requisitionList.items
                .map((requisition) => requisition.site.address.foreign_key)
                .filter((id): id is string => !!id);
            return [...new Set(siteIds)];
        },
        queryKey: ['requisitions', 'recentSites', RECENT_REQUISITIONS],
        // The picker has to stay usable without the recently used addresses, so a failure settles
        // the query instead of holding the list back through a retry.
        retry: false,
    });

    const recentIds = useMemo(() => data ?? [], [data]);
    const recentIdsLoaded = !isPending;

    const recentlyUsedIds = useMemo(() => new Set(recentIds), [recentIds]);

    /**
     * One page of the picker list. The recently used addresses are prepended to page 0 and
     * removed from every page, so each of them shows up exactly once — at the top. They are
     * part of the regular result as well, so dropping them there keeps `total_item_count`
     * describing the merged list without any correction.
     *
     * @ai-generated 2026-08-27T12:14:34+00:00 by matthias.gessinger@synqony.com using Claude Opus 5
     */
    const loadAddressPage = useCallback(
        async (listRequest: ListRequest): Promise<ListInfo<ApiAddress>> => {
            const withRecent = listRequest.page_no === 0 && recentIds.length > 0;
            const [rest, recent] = await Promise.all([
                loadLinkableAddresses(listRequest),
                withRecent ? loadLinkableAddresses(restrictToIds(listRequest, recentIds)) : null,
            ]);

            const others = rest.items.filter((address) => !recentlyUsedIds.has(address.id));
            if (recent === null) {
                return { ...rest, items: others };
            }
            return { ...rest, items: [...inOrderOf(recentIds, recent.items), ...others] };
        },
        [loadLinkableAddresses, recentIds, recentlyUsedIds],
    );

    const {
        items: addresses,
        isLoading,
        isFetching,
        searchValue,
        setSearchValue,
        totalCount,
        hasMore,
        onScrollEvent,
    } = useLinkableSearch<ApiAddress>({
        loadLinkableObjects: loadAddressPage,
        // The recent ids shape page 0, so pages loaded without them must not be reused.
        queryKey: ['requisition', 'site', 'wizard', recentIds.join(',')],
        enabled: recentIdsLoaded,
    });

    return {
        addresses,
        // A disabled query reports no loading state at all, so the wait for the recent ids
        // would otherwise surface as an empty list.
        isLoading: isLoading || !recentIdsLoaded,
        isFetching,
        searchValue,
        setSearchValue,
        totalCount,
        hasMore,
        onScroll: onScrollEvent,
        recentlyUsedIds,
    };
}
